import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { inr, formatApiErrorDetail, imgUrl, HOTEL_CATEGORIES, ROOM_TYPES, BED_SIZES, ROOM_COUNT_OPTIONS, roomGstRate, gstPct, isWholeUnit } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUploader } from "@/components/ImageUploader";
import { DocumentUploader } from "@/components/DocumentUploader";
import { AmenitiesSelect } from "@/components/AmenitiesSelect";
import { RoomAvailabilityCalendar } from "@/components/RoomAvailabilityCalendar";
import { toast } from "sonner";
import { Plus, Trash2, BedDouble, Pencil, CalendarDays } from "lucide-react";

const EMPTY_HOTEL = {
  name: "", description: "", city: "", country: "India", address: "",
  category: "hotel", total_room_count: 0,
  amenities: [], images: [], star_rating: 3,
  contact_phone: "", contact_whatsapp: "",
  legal_name: "", id_documents: [],
};
const EMPTY_ROOM = { name: "", description: "", room_type: "Double", bed_size: "Double", number_of_rooms: 1, max_guests: 2, price_per_night: 2000, images: [], amenities: [] };

export default function HostPropertyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [hotel, setHotel] = useState(EMPTY_HOTEL);
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newRoom, setNewRoom] = useState(EMPTY_ROOM);
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [availRoom, setAvailRoom] = useState(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/hotels/${id}`);
        setHotel(data.hotel);
        setRooms(data.rooms);
      } catch {
        toast.error("Failed to load property");
      }
    })();
  }, [id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (k, v) => setHotel((h) => ({ ...h, [k]: v }));

  const saveHotel = async (e) => {
    e?.preventDefault?.();
    if (isNew && rooms.length === 0) {
      toast.error("Add at least one room before creating the property.");
      setShowAddRoom(true);
      return;
    }
    if (isNew && !(hotel.legal_name || "").trim()) {
      toast.error("Please enter your legal name for verification.");
      return;
    }
    if (isNew && (hotel.id_documents || []).length === 0) {
      toast.error("Please upload at least one ID document for verification.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: hotel.name, description: hotel.description, city: hotel.city,
        country: hotel.country || "India", address: hotel.address,
        category: hotel.category || "hotel",
        total_room_count: Number(hotel.total_room_count) || 0,
        amenities: hotel.amenities || [], images: hotel.images || [],
        star_rating: Number(hotel.star_rating) || 3,
        contact_phone: hotel.contact_phone || "",
        contact_whatsapp: hotel.contact_whatsapp || "",
        legal_name: hotel.legal_name || "",
        id_documents: hotel.id_documents || [],
      };
      if (isNew) {
        const { data } = await api.post("/host/hotels", payload);
        for (const r of rooms) {
          const { _temp, id: _rid, ...roomPayload } = r; // eslint-disable-line no-unused-vars
          await api.post(`/host/hotels/${data.id}/rooms`, roomPayload);
        }
        toast.success("Your property is now live! We'll verify your documents within 7 working days.");
        navigate(`/host/properties/${data.id}`);
      } else {
        await api.put(`/host/hotels/${id}`, payload);
        toast.success("Property updated!");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetRoomForm = () => { setNewRoom(EMPTY_ROOM); setEditingRoomId(null); setShowAddRoom(false); };

  const startEditRoom = (r) => {
    setNewRoom({
      name: r.name || "", description: r.description || "",
      room_type: r.room_type || "Double", bed_size: r.bed_size || r.bed_type || "Double",
      number_of_rooms: r.number_of_rooms ?? r.total_rooms ?? 1,
      max_guests: r.max_guests ?? r.capacity ?? 2,
      price_per_night: r.price_per_night ?? 2000,
      images: r.images || [], amenities: r.amenities || [],
    });
    setEditingRoomId(r.id);
    setShowAddRoom(true);
  };

  const addRoom = async () => {
    const body = {
      name: (newRoom.name || "").trim(), description: newRoom.description || "",
      room_type: newRoom.room_type, bed_size: newRoom.bed_size,
      number_of_rooms: Number(newRoom.number_of_rooms), max_guests: Number(newRoom.max_guests),
      price_per_night: Number(newRoom.price_per_night),
      images: newRoom.images || [], amenities: newRoom.amenities || [],
    };
    if (!body.name) { toast.error("Room name is required"); return; }
    const isTemp = editingRoomId && String(editingRoomId).startsWith("temp-");
    // Edit a saved room
    if (editingRoomId && !isTemp) {
      try {
        const { data } = await api.put(`/host/rooms/${editingRoomId}`, body);
        setRooms((rs) => rs.map((r) => (r.id === editingRoomId ? data : r)));
        resetRoomForm();
        toast.success("Room updated!");
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      return;
    }
    // Edit an unsaved (temp) room
    if (editingRoomId && isTemp) {
      setRooms((rs) => rs.map((r) => (r.id === editingRoomId ? { ...body, id: editingRoomId, _temp: true } : r)));
      resetRoomForm();
      toast.success("Room updated.");
      return;
    }
    // Add new
    if (isNew) {
      setRooms((rs) => [...rs, { ...body, id: `temp-${Date.now()}`, _temp: true }]);
      resetRoomForm();
      toast.success("Room added. It will be saved when you create the property.");
      return;
    }
    try {
      const { data } = await api.post(`/host/hotels/${id}/rooms`, body);
      setRooms((rs) => [...rs, data]);
      resetRoomForm();
      toast.success("Room added!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteRoom = async (roomId) => {
    const room = rooms.find((r) => r.id === roomId);
    if (room?._temp) { setRooms((rs) => rs.filter((r) => r.id !== roomId)); return; }
    if (!window.confirm("Delete this room?")) return;
    try {
      await api.delete(`/host/rooms/${roomId}`);
      setRooms((rs) => rs.filter((r) => r.id !== roomId));
      toast.success("Room deleted");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-3xl font-display font-bold mb-1">{isNew ? "Add a new property" : "Edit property"}</h1>
        <p className="text-[#717171] mb-8">{isNew ? "Tell us about your stay" : hotel.name}</p>

        <form onSubmit={saveHotel} className="bg-white border border-[#EBEBEB] rounded-2xl p-6 space-y-4 mb-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-sm mb-1 block">Property name</Label>
              <Input data-testid="hotel-name-input" required value={hotel.name} onChange={(e) => setField("name", e.target.value)} placeholder="The Ocean Breeze Resort" className="rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm mb-1 block">Description</Label>
              <Textarea data-testid="hotel-description-input" required value={hotel.description} onChange={(e) => setField("description", e.target.value)} rows={4} placeholder="Tell travelers what makes your place special..." className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">City</Label>
              <Input data-testid="hotel-city-input" required value={hotel.city} onChange={(e) => setField("city", e.target.value)} placeholder="Goa" className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Country</Label>
              <Input value={hotel.country} onChange={(e) => setField("country", e.target.value)} className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Property category</Label>
              <Select value={hotel.category || "hotel"} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger data-testid="hotel-category-select" className="rounded-xl">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {HOTEL_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`category-option-${c.value}`}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Total rooms in property</Label>
              <Input data-testid="hotel-total-rooms-input" type="number" min={0} value={hotel.total_room_count} onChange={(e) => setField("total_room_count", Number(e.target.value))} placeholder="e.g. 24" className="rounded-xl" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm mb-1 block">Address</Label>
              <Input data-testid="hotel-address-input" required value={hotel.address} onChange={(e) => setField("address", e.target.value)} placeholder="Street, area..." className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Star rating (1-5)</Label>
              <Input type="number" min={1} max={5} value={hotel.star_rating} onChange={(e) => setField("star_rating", Number(e.target.value))} className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Contact phone (E.164, e.g. +919876543210)</Label>
              <Input data-testid="hotel-phone-input" value={hotel.contact_phone} onChange={(e) => setField("contact_phone", e.target.value)} placeholder="+919876543210" className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">WhatsApp number (optional, falls back to phone)</Label>
              <Input data-testid="hotel-whatsapp-input" value={hotel.contact_whatsapp} onChange={(e) => setField("contact_whatsapp", e.target.value)} placeholder="+919876543210" className="rounded-xl" />
            </div>
            <div>
              <Label className="text-sm mb-1 block">Amenities</Label>
              <AmenitiesSelect value={hotel.amenities || []} onChange={(a) => setField("amenities", a)} testId="hotel-amenities-select" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm mb-1 block">Property photos</Label>
              <ImageUploader testId="hotel-images-uploader" value={hotel.images || []} onChange={(imgs) => setField("images", imgs)} multiple />
              <p className="text-xs text-[#717171] mt-1">Add at least 10 photos. First image is the cover. Min resolution <strong>2048×1080px</strong> (ideal 4000×3000px), max <strong>19MB</strong> per photo — JPG/PNG/WebP.</p>
            </div>
          </div>

          <div className="border border-[#FFE1E7] bg-[#FFF7F9] rounded-2xl p-4 space-y-3">
            <div>
              <h3 className="font-display font-semibold text-[#222] flex items-center gap-2">Host verification</h3>
              <p className="text-xs text-[#717171] mt-0.5">Your property goes <strong className="text-[#222]">live instantly</strong>. We verify your documents within <strong className="text-[#222]">7 working days</strong>; the listing stays live during this period subject to final approval.</p>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Legal name (as on your ID)</Label>
              <Input data-testid="hotel-legalname-input" value={hotel.legal_name || ""} onChange={(e) => setField("legal_name", e.target.value)} placeholder="e.g. Priya Sharma" className="rounded-xl bg-white" />
              <p className="text-xs text-[#717171] mt-1">This is the name shown in the &quot;Hosted by&quot; section on your listing.</p>
            </div>
            <div>
              <Label className="text-sm mb-1 block">Government ID / registration documents</Label>
              <DocumentUploader testId="hotel-id-uploader" value={hotel.id_documents || []} onChange={(docs) => setField("id_documents", docs)} />
              <p className="text-xs text-[#717171] mt-1">Upload at least one ID (PDF or image). Kept private — only Ixitrip admins can view them.</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => navigate("/host/properties")} className="rounded-xl">Cancel</Button>
            <Button data-testid="save-hotel-btn" type="submit" disabled={saving} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">
              {saving ? "Saving..." : (isNew ? "Create property" : "Save changes")}
            </Button>
          </div>
        </form>

        {!isNew && (
          <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6 mb-8">
            <h2 className="font-display font-semibold text-xl mb-3">Boost this property</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <button data-testid="boost-featured-btn" type="button" onClick={async () => { try { const { data } = await api.post(`/host/hotels/${id}/featured/activate`); toast.success(`Featured ${data.featured.status}! ₹2,999/mo after trial.`); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }} className="text-left border border-[#EBEBEB] rounded-2xl p-4 hover:border-[#FF385C] transition-colors">
                <p className="font-display font-semibold">⭐ Featured on home page</p>
                <p className="text-xs text-[#717171] mt-1">₹2,999/mo · first month free · appears in featured carousel</p>
              </button>
              <button data-testid="boost-premium-btn" type="button" onClick={async () => { try { const { data } = await api.post(`/host/hotels/${id}/premium/activate`); toast.success(`Premium ${data.premium.status}! ₹5,999/mo after trial.`); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } }} className="text-left border border-[#EBEBEB] rounded-2xl p-4 hover:border-[#FF385C] transition-colors">
                <p className="font-display font-semibold">👑 Premium (resort/3-star+)</p>
                <p className="text-xs text-[#717171] mt-1">₹5,999/mo · first month free · priority + high-end traveler reach</p>
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#EBEBEB] rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-display font-semibold text-xl flex items-center gap-2"><BedDouble className="w-5 h-5" /> Rooms</h2>
                {isNew && <p className="text-xs text-[#717171] mt-1">Add at least one room — they're saved together when you create the property.</p>}
              </div>
              <Button data-testid="add-room-btn" type="button" onClick={() => { resetRoomForm(); setShowAddRoom(true); }} className="rounded-xl bg-[#222] hover:bg-black text-white">
                <Plus className="w-4 h-4 mr-1" /> Add room
              </Button>
            </div>

            {showAddRoom && (
              <div className="border border-[#EBEBEB] rounded-2xl p-5 mb-4 bg-[#F7F7F9] space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm mb-1 block">Room name</Label>
                    <Input data-testid="room-name-input" placeholder="e.g. Deluxe King" value={newRoom.name} onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">Room type</Label>
                    <Select value={newRoom.room_type} onValueChange={(v) => setNewRoom({ ...newRoom, room_type: v })}>
                      <SelectTrigger data-testid="room-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROOM_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`room-type-${t}`}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">Bed size</Label>
                    <Select value={newRoom.bed_size} onValueChange={(v) => setNewRoom({ ...newRoom, bed_size: v })}>
                      <SelectTrigger data-testid="room-bed-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{BED_SIZES.map((b) => <SelectItem key={b} value={b} data-testid={`bed-size-${b}`}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">{isWholeUnit(hotel.category) ? "Number of units (villas)" : "Number of rooms"}</Label>
                    <Select value={String(newRoom.number_of_rooms)} onValueChange={(v) => setNewRoom({ ...newRoom, number_of_rooms: Number(v) })}>
                      <SelectTrigger data-testid="room-count-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">{ROOM_COUNT_OPTIONS.map((n) => <SelectItem key={n} value={String(n)} data-testid={`room-count-${n}`}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm mb-1 block">{isWholeUnit(hotel.category) ? "How many guests allowed in the whole place?" : "How many guests allowed in one room?"}</Label>
                    <Input data-testid="room-maxguests-input" type="number" min={1} max={50} value={newRoom.max_guests} onChange={(e) => setNewRoom({ ...newRoom, max_guests: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Room photos</Label>
                  <ImageUploader testId="room-images-uploader" value={newRoom.images} onChange={(imgs) => setNewRoom({ ...newRoom, images: imgs })} multiple />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Room description</Label>
                  <Textarea placeholder="Room description" value={newRoom.description} onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })} className="rounded-xl" />
                </div>

                <div className="border-t border-[#EBEBEB] pt-4">
                  <h3 className="font-display font-semibold mb-2">Pricing</h3>
                  <div className="grid sm:grid-cols-2 gap-3 items-end">
                    <div>
                      <Label className="text-sm mb-1 block">{isWholeUnit(hotel.category) ? "Price / night (whole villa)" : "Price / night (per room)"}</Label>
                      <Input data-testid="room-price-input" type="number" min={0} value={newRoom.price_per_night} onChange={(e) => setNewRoom({ ...newRoom, price_per_night: e.target.value })} className="rounded-xl" />
                    </div>
                    <div className="text-sm text-[#717171] pb-2" data-testid="room-gst-note">
                      Taxes extra: <strong className="text-[#222]">{gstPct(roomGstRate(newRoom.price_per_night))} GST</strong>
                      <span className="block text-xs">applied on tariff at booking; +₹299 Ixitrip convenience fee.</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={resetRoomForm} className="rounded-xl">Cancel</Button>
                  <Button data-testid="save-room-btn" type="button" onClick={addRoom} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">{editingRoomId ? "Update room" : "Save room"}</Button>
                </div>
              </div>
            )}

            {rooms.length === 0 ? (
              <p className="text-[#717171] py-4">No rooms yet. Add one to start receiving bookings.</p>
            ) : (
              <div className="divide-y divide-[#EBEBEB]">
                {rooms.map((r) => (
                  <div key={r.id} className="flex items-center gap-4 py-4">
                    <img src={r.images?.[0] ? imgUrl(r.images[0]) : "https://images.pexels.com/photos/237371/pexels-photo-237371.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300"} alt="" className="w-20 h-16 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className="font-semibold">{r.name}</p>
                      <p className="text-xs text-[#717171]">{r.room_type || "Double"} · {r.bed_size || r.bed_type || "Double"} bed · up to {r.max_guests ?? r.capacity ?? 2} guests · {r.number_of_rooms ?? r.total_rooms ?? 1} unit(s)</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display font-bold">{inr(r.price_per_night)}</p>
                      <p className="text-xs text-[#717171]">/ night</p>
                    </div>
                    <button data-testid={`edit-room-${r.id}`} onClick={() => startEditRoom(r)} className="text-[#222] p-2 hover:bg-[#F0F0F0] rounded-lg" title="Edit room">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {!r._temp && (
                      <button data-testid={`avail-room-${r.id}`} onClick={() => setAvailRoom(r)} className="text-[#008489] p-2 hover:bg-[#E6F4F4] rounded-lg" title="Availability & pricing calendar">
                        <CalendarDays className="w-4 h-4" />
                      </button>
                    )}
                    <button data-testid={`delete-room-${r.id}`} onClick={() => deleteRoom(r.id)} className="text-[#C13515] p-2 hover:bg-[#FFEBEE] rounded-lg" title="Delete room">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
      </div>
      <RoomAvailabilityCalendar
        open={!!availRoom}
        onOpenChange={(o) => !o && setAvailRoom(null)}
        roomId={availRoom?.id}
        roomName={availRoom?.name}
        basePrice={availRoom?.price_per_night}
      />
    </div>
  );
}
